import React from "react";
import { formatCents, formatNegativeCurrency, roundCents } from "@/lib/deductionSummaryFormat";
import {
  hasNonMonthlyPayCycle,
  calculatePerPaycheckAmount,
  calculateAnnualDeductionAmount,
  getPayCycleLabelCapitalized,
} from "@/lib/payrollUtils";
import { amountOrPlaceholder } from "@/lib/loadingPlaceholder";
import { totalPricedMonthly } from "@/lib/planPriceDisplay";

export interface DeductionLineItem {
  label: string;
  /** null = can't be priced (DEV-382): shown as such, and the totals are withheld. */
  monthlyAmount: number | null;
}

export interface DeductionSummaryContentProps {
  items: DeductionLineItem[];
  totalMonthly?: number;
  payCycle?: string | null;
  showTaxSavings?: boolean;
  taxBracket?: number;
  emptyMessage?: React.ReactNode;
  /** Enrollments still loading: totals render as a dash, never "$0.00" (BUG-008). */
  isLoading?: boolean;
}

export function DeductionSummaryContent({
  items,
  totalMonthly,
  payCycle,
  showTaxSavings = false,
  taxBracket = 0.25,
  emptyMessage,
  isLoading = false,
}: DeductionSummaryContentProps) {
  const priced = totalPricedMonthly(items);
  const total = totalMonthly ?? priced.totalMonthly;
  const isNonMonthly = hasNonMonthlyPayCycle(payCycle);
  // Same figure as each benefit card's "/yr": per-paycheck x periods (DEV-356).
  const annual = calculateAnnualDeductionAmount(total, payCycle);
  // DEV-382: an unpriceable plan is never totalled as $0 — totals are withheld.
  const incomplete = !isLoading && priced.unpricedPlans.length > 0;
  const money = (format: () => string) =>
    incomplete ? "Unavailable" : amountOrPlaceholder(isLoading, format);

  if (items.length === 0 && emptyMessage && !isLoading) {
    return <>{emptyMessage}</>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Left column: per-paycheck or monthly line items */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
          {isNonMonthly
            ? `Per ${getPayCycleLabelCapitalized(payCycle)} Paycheck`
            : "Current Deductions"}
        </h3>
        <div className="space-y-2 text-sm">
          {items.map((it, idx) => {
            const displayAmt =
              it.monthlyAmount == null
                ? null
                : isNonMonthly
                  ? calculatePerPaycheckAmount(it.monthlyAmount, payCycle)
                  : it.monthlyAmount;
            return (
              <div key={idx} className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  {it.label}
                </span>
                {displayAmt == null ? (
                  <span className="font-medium text-amber-700 dark:text-amber-400">Can't price</span>
                ) : (
                  <span className="font-medium">${roundCents(displayAmt).toFixed(2)}</span>
                )}
              </div>
            );
          })}
          <div className="border-t pt-2 flex justify-between font-semibold">
            <span>
              Total{" "}
              {isNonMonthly
                ? getPayCycleLabelCapitalized(payCycle)
                : "Monthly"}
            </span>
            <span data-testid="deduction-summary-total" data-loading={isLoading ? "true" : undefined}>
              {money(() =>
                `$${isNonMonthly
                  ? roundCents(calculatePerPaycheckAmount(total, payCycle)).toFixed(2)
                  : roundCents(total).toFixed(2)}`,
              )}
            </span>
          </div>
          {incomplete ? (
            <div className="text-xs text-amber-700 dark:text-amber-400 mt-1" data-testid="deduction-summary-unpriced">
              Can't total: {priced.unpricedPlans.join(", ")} couldn't be priced — see the benefit card for the reason.
            </div>
          ) : isNonMonthly && (
            <div className="text-xs text-muted-foreground mt-1">
              ({money(() => `$${roundCents(total).toFixed(2)}`)}/month)
            </div>
          )}
        </div>
      </div>

      {/* Right column: annual summary */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
          Annual Summary
        </h3>
        <div className="space-y-2 text-sm">
          {/* When the left column has no line items its total row starts with
              a divider — mirror that here so the two columns stay vertically
              aligned. Bold only when this is the final total (no tax-savings
              rows below it). */}
          <div
            className={`${items.length === 0 ? "border-t pt-2 " : ""}flex justify-between ${
              showTaxSavings ? "" : "font-semibold"
            }`}
          >
            <span className={showTaxSavings ? "text-gray-600 dark:text-gray-400" : ""}>
              Total Annual Cost
            </span>
            <span className={showTaxSavings ? "font-medium" : ""}>
              {money(() => formatCents(annual))}
            </span>
          </div>
          {showTaxSavings && (() => {
            // Round savings to cents so Total - Savings = Net exactly as displayed.
            const taxSavings = roundCents(annual * taxBracket);
            const net = Math.max(0, roundCents(annual - taxSavings));
            return (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    Tax Savings (Est.)
                  </span>
                  <span className="text-green-700">
                    {money(() => formatNegativeCurrency(taxSavings))}
                  </span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Net Annual Cost</span>
                  <span>{money(() => formatCents(net))}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Estimated tax savings based on {Math.round(taxBracket * 100)}% tax bracket
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
